<template>
  <div :class="['activity-card__active', type, `card-active-${activeColorName}`]">
    <div class="card-top-header">
      <div class="header-left">
        <span class="radio-dot"></span>
        <span class="card-state-label font-weight-bold font-raleway">
          {{ customLabel ? customLabel : title }}
        </span>
      </div>
      <div v-if="isLoading" class="header-right">
        <b-spinner small type="border" class="text-white opacity-8" />
      </div>
      <div v-else class="header-right">
        <feather-icon :icon="statusIcon" size="16" class="text-white opacity-8" />
      </div>
    </div>

    <div class="card-timer-body text-center">
      <div v-if="date" class="timer-date-stamp">{{ date }}</div>
      <div class="timer-display">
        {{ formattedHour }} : {{ formattedMinute }} : {{ formattedSecond }}
      </div>
      <div class="timer-labels">
        <span>H</span>
        <span>M</span>
        <span>S</span>
      </div>
    </div>

    <div v-if="showAddButton" class="card-bottom-button">
      <feather-icon icon="PlusIcon" size="14" class="mr-25" />
      <span>{{ $t("ProductionProgress.Add") }}</span>
    </div>
  </div>
</template>

<script>
import { BSpinner } from "bootstrap-vue";
import FeatherIcon from "@core/components/feather-icon/FeatherIcon.vue";

export default {
  name: "ActivityCardActive",
  components: {
    BSpinner,
    FeatherIcon,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    type: {
      type: String,
      required: true,
    },
    customLabel: {
      type: String,
      default: null,
    },
    showAddButton: {
      type: Boolean,
      default: false,
    },
    timer: {
      type: Object,
      default: () => ({
        hour: null,
        minute: null,
        second: null,
      }),
    },
    date: {
      type: String,
      default: null,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    activeColorName() {
      switch (this.type) {
        case "setup":
          return "slate";
        case "start":
          return "orange";
        case "stop":
          return "red";
        case "finish":
          return "green";
        default:
          return "orange";
      }
    },
    statusIcon() {
      switch (this.type) {
        case "setup":
          return "SlidersIcon";
        case "start":
          return "PlayIcon";
        case "stop":
          return "PauseIcon";
        case "finish":
          return "CheckIcon";
        default:
          return "PlayIcon";
      }
    },
    formattedHour() {
      const h = this.timer?.hour;
      if (h === null || h === undefined) return "00";
      return String(h).padStart(2, "0");
    },
    formattedMinute() {
      const m = this.timer?.minute;
      if (m === null || m === undefined) return "00";
      return String(m).padStart(2, "0");
    },
    formattedSecond() {
      const s = this.timer?.second;
      if (s === null || s === undefined) return "00";
      return String(s).padStart(2, "0");
    },
  },
};
</script>

<style lang="scss">
.activity-card__active {
  width: 100%;
  height: 100%;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-sizing: border-box;
  color: #ffffff;
  overflow: hidden;
  position: relative;

  &.card-active-slate {
    background: #607d8b !important;
    border: 1px solid #455a64 !important;
  }

  &.card-active-orange {
    background: #e68a00 !important;
    border: 1px solid #d47e00 !important;
  }

  &.card-active-red {
    background: #ea5455 !important;
    border: 1px solid #d64546 !important;
  }

  &.card-active-green {
    background: #28c76f !important;
    border: 1px solid #20a359 !important;
  }

  .card-top-header {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 14px 0 14px;

    .header-left {
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .card-state-label {
      font-size: 15px;
      font-weight: 700;
      color: #ffffff;
      font-family: "Raleway", sans-serif;
    }

    .radio-dot {
      width: 9px;
      height: 9px;
      background: #ffffff;
      border-radius: 50%;
      display: inline-block;
      box-shadow: none;
    }

    .header-right {
      display: flex;
      align-items: center;
    }

    .opacity-8 {
      opacity: 0.85;
    }
  }

  .card-timer-body {
    padding: 4px 10px;
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin: auto 0;

    .timer-date-stamp {
      font-size: 12px;
      opacity: 0.9;
      font-family: "Raleway", sans-serif;
      margin-bottom: 4px;
      color: #ffffff;
      font-weight: 500;
    }

    .timer-display {
      font-size: 34px;
      line-height: 40px;
      letter-spacing: 2px;
      font-family: monospace, sans-serif;
      font-weight: 700;
      color: #ffffff;
    }

    .timer-labels {
      display: flex;
      justify-content: center;
      font-size: 10px;
      opacity: 0.85;
      gap: 22px;
      margin-top: 2px;
      color: #ffffff;
      font-family: "Raleway", sans-serif;
      font-weight: 600;
    }
  }

  .card-bottom-button {
    width: 100%;
    padding: 8px 0;
    background: rgba(0, 0, 0, 0.15);
    color: #ffffff;
    font-weight: 700;
    font-size: 14px;
    font-family: "Raleway", sans-serif;
    border-top: 1px solid rgba(255, 255, 255, 0.2);
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.2s ease;

    &:hover {
      background: rgba(0, 0, 0, 0.25);
    }
  }
}

@media (max-width: 480px) {
  .activity-card__active {
    .card-top-header {
      padding: 8px 10px 0 10px;

      .card-state-label {
        font-size: 13px;
      }
    }

    .card-timer-body {
      .timer-date-stamp {
        font-size: 11px;
      }

      .timer-display {
        font-size: 26px;
        line-height: 30px;
        letter-spacing: 1px;
      }

      .timer-labels {
        gap: 20px;
        font-size: 9px;
      }
    }

    .card-bottom-button {
      padding: 8px 0;
      font-size: 12px;
    }
  }
}
</style>
